/** Signal-driven transition generation.
 *
 *  The manual feature has exactly one generator today (`assignRandomTransitions`
 *  — random kind at every existing cut). This module adds a SIGNAL-driven one,
 *  fed by the `video_signals_url` JSON the worker produces (audio/motion
 *  envelopes, keyframe-decoded scene cuts, energy peaks, a coarse content-type
 *  classification).
 *
 *  Everything here is pure: no React, no `Math.random`, no `Date.now()`. Kind
 *  selection cycles deterministically through the content-type profile, so the
 *  same signals always produce the same edit.
 *
 *  All input times are SOURCE seconds, matching `clipIntervals` and
 *  `KeyframeTransition.atTime`.
 *
 *  Validation is never reimplemented: every produced list goes back through
 *  `sanitizeTransitions` / `sanitizeKeyframeTransitions` from `./types` +
 *  `./keyframeCuts`, which own the duration clamp, the kind whitelist, the
 *  duplicate rule (first wins) and the sort order. */

import {
  clampTransitionDuration,
  sanitizeTransitions,
  type KeyframeTransition,
  type SceneTransition,
  type TransitionKind,
} from './types';
import { round3, sanitizeKeyframeTransitions } from './keyframeCuts';

/* ------------------------------------------------------------------ *
 * Signals payload
 * ------------------------------------------------------------------ */

/** `[startSec, endSec, intensity0to1]`. */
export type SignalPeak = [number, number, number];

export interface VideoSignalsEnvelopes {
  /** Empty array = "no such stream". All-zeros = "measured, nothing happened".
   *  Both are preserved verbatim — this module never collapses one into the
   *  other. */
  audio_rms: number[];
  spectral_flux: number[];
  motion_energy: number[];
}

export interface VideoSignals {
  version: number;
  duration: number;
  sample_hz: number;
  signals: VideoSignalsEnvelopes;
  /** KEYFRAME-only decode, so COARSE (keyframes are typically 2-10s apart).
   *  Never treated as frame-accurate anywhere in this module. */
  scene_cuts: number[];
  peaks: { audio: SignalPeak[]; motion: SignalPeak[] };
  content_type: ContentType;
  content_type_confidence: number | null;
  meta: Record<string, unknown>;
}

export const VIDEO_SIGNALS_VERSION = 1;

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

const numberArray = (raw: unknown): number[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((v) => (isFiniteNumber(v) ? v : 0));
};

const peakArray = (raw: unknown): SignalPeak[] => {
  if (!Array.isArray(raw)) return [];
  const out: SignalPeak[] = [];
  for (const item of raw) {
    if (!Array.isArray(item) || item.length < 3) continue;
    const [start, end, intensity] = item;
    if (!isFiniteNumber(start) || !isFiniteNumber(end) || !isFiniteNumber(intensity)) continue;
    if (start < 0 || end < start) continue;
    out.push([start, end, Math.min(1, Math.max(0, intensity))]);
  }
  return out;
};

/** Parse an unknown blob (fetched JSON, a cached value, `null`) into a usable
 *  `VideoSignals`, or `null` when it is unusable. Guards: non-object, missing
 *  or `!== 1` version, missing/NaN duration, missing arrays. Never throws. */
export function parseVideoSignals(raw: unknown): VideoSignals | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (r.version !== VIDEO_SIGNALS_VERSION) return null;
  if (!isFiniteNumber(r.duration) || r.duration <= 0) return null;

  const signals = (r.signals && typeof r.signals === 'object' ? r.signals : {}) as Record<string, unknown>;
  const peaks = (r.peaks && typeof r.peaks === 'object' ? r.peaks : {}) as Record<string, unknown>;

  const sceneCuts = Array.isArray(r.scene_cuts)
    ? r.scene_cuts.filter((v): v is number => isFiniteNumber(v) && v >= 0 && v <= (r.duration as number))
    : [];

  return {
    version: VIDEO_SIGNALS_VERSION,
    duration: r.duration,
    sample_hz: isFiniteNumber(r.sample_hz) && r.sample_hz > 0 ? r.sample_hz : 1,
    signals: {
      audio_rms: numberArray(signals.audio_rms),
      spectral_flux: numberArray(signals.spectral_flux),
      motion_energy: numberArray(signals.motion_energy),
    },
    scene_cuts: [...sceneCuts].sort((a, b) => a - b),
    peaks: { audio: peakArray(peaks.audio), motion: peakArray(peaks.motion) },
    content_type: normalizeContentType(r.content_type),
    content_type_confidence: isFiniteNumber(r.content_type_confidence)
      ? Math.min(1, Math.max(0, r.content_type_confidence))
      : null,
    meta: (r.meta && typeof r.meta === 'object' ? r.meta : {}) as Record<string, unknown>,
  };
}

/* ------------------------------------------------------------------ *
 * Content-type profiles (behavior d)
 * ------------------------------------------------------------------ */

export const CONTENT_TYPES = ['soccer', 'gameplay', 'vlog', 'podcast', 'unknown'] as const;
export type ContentType = (typeof CONTENT_TYPES)[number];

export const normalizeContentType = (raw: unknown): ContentType =>
  typeof raw === 'string' && (CONTENT_TYPES as readonly string[]).includes(raw)
    ? (raw as ContentType)
    : 'unknown';

/** Below this the classifier is treated as a coin flip and we fall back to the
 *  conservative `unknown` profile. */
export const MIN_CONTENT_TYPE_CONFIDENCE = 0.5;

export interface ContentTypeTransitionProfile {
  /** Kinds allowed at a hard interval cut (two different pieces of footage) —
   *  the overlap family (crossfade/slide/wipe) is legal here. Cycled in order. */
  cutKinds: TransitionKind[];
  /** Kinds allowed INSIDE continuous footage (keyframe boundaries, scene cuts).
   *  Transform family only — the sanitizer drops anything else. Cycled in order. */
  transformKinds: TransitionKind[];
  durationSec: number;
  /** Cap on behavior (a): transitions placed at detected scene cuts. */
  maxSceneCutTransitions: number;
  /** Cap on behavior (b): transitions placed at energy peaks. */
  maxEmphasisTransitions: number;
}

/** Exported so it is testable and tunable without touching the generator. */
export const CONTENT_TYPE_TRANSITION_PROFILES: Record<ContentType, ContentTypeTransitionProfile> = {
  // Punchy: fast action, hard camera language.
  soccer: {
    cutKinds: ['whip-pan', 'zoom-in', 'camera-move'],
    transformKinds: ['whip-pan', 'zoom-in', 'camera-move'],
    durationSec: 0.25,
    maxSceneCutTransitions: 24,
    maxEmphasisTransitions: 6,
  },
  gameplay: {
    cutKinds: ['whip-pan', 'zoom-in', 'zoom-out', 'camera-move'],
    transformKinds: ['whip-pan', 'zoom-in', 'zoom-out', 'camera-move'],
    durationSec: 0.25,
    maxSceneCutTransitions: 24,
    maxEmphasisTransitions: 6,
  },
  // Softer: crossfade is legal at real cuts only; the keyframe track gets the
  // transform-family equivalents.
  vlog: {
    cutKinds: ['crossfade', 'dip-to-white', 'blur'],
    transformKinds: ['blur', 'dip-to-white', 'camera-move'],
    durationSec: 0.45,
    maxSceneCutTransitions: 12,
    maxEmphasisTransitions: 4,
  },
  // Minimal/none: talking heads do not want camera language.
  podcast: {
    cutKinds: ['dip-to-black'],
    transformKinds: ['dip-to-black'],
    durationSec: 0.3,
    maxSceneCutTransitions: 0,
    maxEmphasisTransitions: 0,
  },
  // Conservative default: a single subtle kind, few placements.
  unknown: {
    cutKinds: ['dip-to-black'],
    transformKinds: ['dip-to-black'],
    durationSec: 0.3,
    maxSceneCutTransitions: 6,
    maxEmphasisTransitions: 2,
  },
};

/** Pick the profile for a (possibly unknown / low-confidence) classification.
 *  An explicit `contentType` argument wins over the one baked into the signals
 *  JSON at the call site; both are normalized here. */
export function resolveContentTypeProfile(
  contentType: unknown,
  confidence?: number | null,
): { contentType: ContentType; profile: ContentTypeTransitionProfile } {
  let resolved = normalizeContentType(contentType);
  if (
    resolved !== 'unknown' &&
    isFiniteNumber(confidence) &&
    confidence < MIN_CONTENT_TYPE_CONFIDENCE
  ) {
    resolved = 'unknown';
  }
  return { contentType: resolved, profile: CONTENT_TYPE_TRANSITION_PROFILES[resolved] };
}

/* ------------------------------------------------------------------ *
 * Shared helpers
 * ------------------------------------------------------------------ */

export interface ClipIntervalLike {
  startTime: number;
  endTime: number;
}

/** A transition must not straddle an interval edge, so a candidate time has to
 *  sit at least this far inside the interval. */
export const INTERVAL_EDGE_MARGIN_SEC = 0.25;

/** How close a peak must be to an interval boundary to be treated as "at that
 *  cut" rather than "inside a scene". */
export const PEAK_CUT_MATCH_TOLERANCE_SEC = 0.5;

/** Minimum spacing between two emphasis placements, so a burst of peaks does
 *  not carpet a couple of seconds with transitions. */
export const EMPHASIS_MIN_SPACING_SEC = 1.5;

/** Default tolerance for behavior (c). Scene cuts are keyframe-coarse, so this
 *  is generous-but-bounded rather than a few frames. */
export const SNAP_TOLERANCE_SEC = 0.4;

/** An interval shorter than this after snapping is rejected. */
export const MIN_SNAPPED_INTERVAL_SEC = 0.2;

const validIntervals = (intervals: ClipIntervalLike[] | null | undefined): ClipIntervalLike[] =>
  Array.isArray(intervals)
    ? intervals.filter(
        (iv) =>
          !!iv &&
          isFiniteNumber(iv.startTime) &&
          isFiniteNumber(iv.endTime) &&
          iv.startTime >= 0 &&
          iv.endTime > iv.startTime,
      )
    : [];

/** Snap a candidate time onto the nearest entry of `targets` within
 *  `tolerance`. Returns `null` when nothing is close enough. Ties go to the
 *  EARLIER target (deterministic). */
function snapToNearest(time: number, targets: number[], tolerance: number): number | null {
  let best: number | null = null;
  let bestDist = Infinity;
  for (const target of targets) {
    if (!isFiniteNumber(target)) continue;
    const d = Math.abs(target - time);
    // Float-tolerant compare: 10.4 - 10 is 0.40000000000000036, which must
    // still count as "exactly at the 0.4 tolerance".
    if (d > tolerance + 1e-9) continue;
    if (d < bestDist || (d === bestDist && best !== null && target < best)) {
      bestDist = d;
      best = target;
    }
  }
  return best;
}

/** Deterministic kind cycling — no rng, so the same signals always yield the
 *  same edit (renders stay reproducible, matching the assign-at-click rule). */
const pickKind = (kinds: TransitionKind[], ordinal: number): TransitionKind | null =>
  kinds.length === 0 ? null : kinds[ordinal % kinds.length];

/* ------------------------------------------------------------------ *
 * (a) Keyframe transitions at real scene cuts
 * ------------------------------------------------------------------ */

/** Every scene cut that falls strictly INSIDE a kept interval (never in a
 *  removed gap, never on an edge). Deduped to 3 decimals, sorted ascending. */
export function sceneCutsInsideIntervals(
  sceneCuts: number[] | null | undefined,
  intervals: ClipIntervalLike[] | null | undefined,
  marginSec: number = INTERVAL_EDGE_MARGIN_SEC,
): number[] {
  if (!Array.isArray(sceneCuts)) return [];
  const ivs = validIntervals(intervals);
  if (ivs.length === 0) return [];
  const margin = isFiniteNumber(marginSec) && marginSec >= 0 ? marginSec : INTERVAL_EDGE_MARGIN_SEC;
  const seen = new Set<number>();
  const out: number[] = [];
  for (const cut of sceneCuts) {
    if (!isFiniteNumber(cut) || cut < 0) continue;
    const inside = ivs.some((iv) => cut > iv.startTime + margin && cut < iv.endTime - margin);
    if (!inside) continue;
    const key = round3(cut);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out.sort((a, b) => a - b);
}

export interface SceneCutTransitionsInput {
  sceneCuts: number[] | null | undefined;
  clipIntervals: ClipIntervalLike[] | null | undefined;
  profile: ContentTypeTransitionProfile;
  /** When provided, each cut is snapped onto the nearest of these SOURCE times
   *  (the live crop-keyframe boundaries) and dropped when none is within
   *  `snapToleranceSec`. Omit to place transitions at the raw cut times. */
  snapTargets?: number[];
  snapToleranceSec?: number;
  marginSec?: number;
  durationSec?: number;
  maxTransitions?: number;
}

/** Behavior (a): a duration-preserving TRANSFORM transition at each real scene
 *  cut inside the kept footage. Nothing is added or removed from the timeline,
 *  so subtitles and clip length are untouched — the safe default. */
export function assignSceneCutKeyframeTransitions(
  {
    sceneCuts,
    clipIntervals,
    profile,
    snapTargets,
    snapToleranceSec = SNAP_TOLERANCE_SEC,
    marginSec = INTERVAL_EDGE_MARGIN_SEC,
    durationSec,
    maxTransitions,
  }: SceneCutTransitionsInput,
): KeyframeTransition[] {
  const limit = Math.max(
    0,
    Math.floor(
      isFiniteNumber(maxTransitions) ? maxTransitions : profile.maxSceneCutTransitions,
    ),
  );
  if (limit === 0 || profile.transformKinds.length === 0) return [];

  const duration = clampTransitionDuration(
    isFiniteNumber(durationSec) ? durationSec : profile.durationSec,
  );

  const candidates = sceneCutsInsideIntervals(sceneCuts, clipIntervals, marginSec);
  const raw: KeyframeTransition[] = [];
  for (const cut of candidates) {
    if (raw.length >= limit) break;
    const at = snapTargets ? snapToNearest(cut, snapTargets, snapToleranceSec) : cut;
    if (at === null) continue;
    const kind = pickKind(profile.transformKinds, raw.length);
    if (!kind) break;
    raw.push({ atTime: round3(at), kind, durationSec: duration });
  }
  // Reuse the shared validator: transform-only, dedupe (first wins), clamp,
  // sorted. Boundaries are the times we just emitted, so nothing is snapped
  // away here — this is a validation pass, not a second snap.
  return sanitizeKeyframeTransitions(raw, raw.map((t) => t.atTime));
}

/* ------------------------------------------------------------------ *
 * (b) Emphasis transitions at energy peaks
 * ------------------------------------------------------------------ */

export interface SelectPeaksOptions {
  /** Maximum number of peaks to keep. */
  limit: number;
  /** Peaks weaker than this are ignored entirely. */
  minIntensity?: number;
  /** Minimum gap (seconds) between two kept peak starts. */
  minSpacingSec?: number;
}

/** Merge + rank peak streams. Returned in SELECTION order: strongest first,
 *  ties broken by earlier start (deterministic). A greedy spacing filter keeps
 *  a burst of adjacent peaks from collapsing into a cluster of transitions. */
export function selectTopPeaks(
  peaks: Array<SignalPeak[] | null | undefined>,
  { limit, minIntensity = 0, minSpacingSec = EMPHASIS_MIN_SPACING_SEC }: SelectPeaksOptions,
): SignalPeak[] {
  const max = Math.max(0, Math.floor(isFiniteNumber(limit) ? limit : 0));
  if (max === 0) return [];
  const spacing = isFiniteNumber(minSpacingSec) && minSpacingSec > 0 ? minSpacingSec : 0;
  const floor = isFiniteNumber(minIntensity) ? minIntensity : 0;

  const flat: SignalPeak[] = [];
  for (const stream of peaks) {
    if (!Array.isArray(stream)) continue;
    for (const p of stream) {
      if (!Array.isArray(p) || p.length < 3) continue;
      const [start, end, intensity] = p;
      if (!isFiniteNumber(start) || !isFiniteNumber(end) || !isFiniteNumber(intensity)) continue;
      if (start < 0 || end < start) continue;
      if (intensity < floor) continue;
      flat.push([start, end, intensity]);
    }
  }

  flat.sort((a, b) => (b[2] - a[2]) || (a[0] - b[0]) || (a[1] - b[1]));

  const kept: SignalPeak[] = [];
  for (const p of flat) {
    if (kept.length >= max) break;
    if (spacing > 0 && kept.some((k) => Math.abs(k[0] - p[0]) < spacing)) continue;
    kept.push(p);
  }
  return kept;
}

export interface EmphasisTransitionsInput {
  peaks: Array<SignalPeak[] | null | undefined>;
  clipIntervals: ClipIntervalLike[] | null | undefined;
  profile: ContentTypeTransitionProfile;
  snapTargets?: number[];
  snapToleranceSec?: number;
  marginSec?: number;
  durationSec?: number;
  maxTransitions?: number;
  minIntensity?: number;
  minSpacingSec?: number;
  cutMatchToleranceSec?: number;
}

export interface EmphasisTransitionsResult {
  /** Peaks that landed on an existing interval cut. */
  sceneTransitions: SceneTransition[];
  /** Peaks that landed inside continuous footage. */
  keyframeTransitions: KeyframeTransition[];
  /** How many peaks produced a placement. */
  used: number;
}

/** Behavior (b): the top-N most intense peaks get a transition — the thing that
 *  makes a soccer clip feel edited when the crowd roars and nobody says a
 *  distinguishing word. A peak that lands on an existing interval cut becomes a
 *  SceneTransition; one inside continuous footage becomes a duration-preserving
 *  KeyframeTransition. */
export function assignEmphasisTransitions(
  {
    peaks,
    clipIntervals,
    profile,
    snapTargets,
    snapToleranceSec = SNAP_TOLERANCE_SEC,
    marginSec = INTERVAL_EDGE_MARGIN_SEC,
    durationSec,
    maxTransitions,
    minIntensity,
    minSpacingSec,
    cutMatchToleranceSec = PEAK_CUT_MATCH_TOLERANCE_SEC,
  }: EmphasisTransitionsInput,
): EmphasisTransitionsResult {
  const empty: EmphasisTransitionsResult = {
    sceneTransitions: [],
    keyframeTransitions: [],
    used: 0,
  };
  const limit = Math.max(
    0,
    Math.floor(isFiniteNumber(maxTransitions) ? maxTransitions : profile.maxEmphasisTransitions),
  );
  if (limit === 0) return empty;

  const ivs = validIntervals(clipIntervals);
  if (ivs.length === 0) return empty;

  const duration = clampTransitionDuration(
    isFiniteNumber(durationSec) ? durationSec : profile.durationSec,
  );
  const selected = selectTopPeaks(peaks, { limit, minIntensity, minSpacingSec });
  if (selected.length === 0) return empty;

  // The boundary between interval i and interval i+1, in SOURCE seconds.
  const cutTimes = ivs.slice(1).map((iv) => iv.startTime);

  const rawScene: SceneTransition[] = [];
  const rawKeyframe: KeyframeTransition[] = [];
  let ordinal = 0;

  for (const [start] of selected) {
    // Strict `<` with ascending iteration keeps the EARLIEST cut on ties.
    let cutIndex = -1;
    let bestDist = Infinity;
    cutTimes.forEach((t, i) => {
      const d = Math.abs(t - start);
      if (d <= cutMatchToleranceSec && d < bestDist) {
        bestDist = d;
        cutIndex = i;
      }
    });

    if (cutIndex >= 0) {
      const kind = pickKind(profile.cutKinds, ordinal);
      if (!kind) continue;
      rawScene.push({ afterSceneIndex: cutIndex, kind, durationSec: duration });
      ordinal += 1;
      continue;
    }

    const inside = ivs.some((iv) => start > iv.startTime + marginSec && start < iv.endTime - marginSec);
    if (!inside) continue;
    const at = snapTargets ? snapToNearest(start, snapTargets, snapToleranceSec) : start;
    if (at === null) continue;
    const kind = pickKind(profile.transformKinds, ordinal);
    if (!kind) continue;
    rawKeyframe.push({ atTime: round3(at), kind, durationSec: duration });
    ordinal += 1;
  }

  const sceneTransitions = sanitizeTransitions(rawScene, ivs.length);
  const keyframeTransitions = sanitizeKeyframeTransitions(
    rawKeyframe,
    rawKeyframe.map((t) => t.atTime),
  );
  return {
    sceneTransitions,
    keyframeTransitions,
    used: sceneTransitions.length + keyframeTransitions.length,
  };
}

/* ------------------------------------------------------------------ *
 * (c) Snap user cuts to scene boundaries
 * ------------------------------------------------------------------ */

export interface SnapAdjustment {
  intervalIndex: number;
  edge: 'start' | 'end';
  from: number;
  to: number;
  deltaSec: number;
}

export interface SnapIntervalsInput {
  intervals: ClipIntervalLike[] | null | undefined;
  sceneCuts: number[] | null | undefined;
  /** Scene cuts are keyframe-coarse, so the default window is generous but
   *  bounded. Anything beyond it is left alone. */
  toleranceSec?: number;
  minIntervalSec?: number;
}

/** Behavior (c): nudge each interval edge onto the nearest detected scene cut
 *  within `toleranceSec`. PURE — the caller gets the adjustment list so the UI
 *  can preview it and undo it; nothing is mutated. Adjustments that would
 *  invert or over-shorten an interval are dropped. */
export function snapIntervalsToSceneCuts(
  {
    intervals,
    sceneCuts,
    toleranceSec = SNAP_TOLERANCE_SEC,
    minIntervalSec = MIN_SNAPPED_INTERVAL_SEC,
  }: SnapIntervalsInput,
): SnapAdjustment[] {
  const ivs = validIntervals(intervals);
  if (ivs.length === 0 || !Array.isArray(sceneCuts) || sceneCuts.length === 0) return [];
  const tolerance = isFiniteNumber(toleranceSec) && toleranceSec > 0 ? toleranceSec : SNAP_TOLERANCE_SEC;
  const minLen = isFiniteNumber(minIntervalSec) && minIntervalSec > 0 ? minIntervalSec : MIN_SNAPPED_INTERVAL_SEC;
  const cuts = sceneCuts.filter((c): c is number => isFiniteNumber(c) && c >= 0);
  if (cuts.length === 0) return [];

  const out: SnapAdjustment[] = [];
  ivs.forEach((iv, intervalIndex) => {
    let start = iv.startTime;
    let end = iv.endTime;

    const snappedStart = snapToNearest(start, cuts, tolerance);
    if (snappedStart !== null && round3(snappedStart) !== round3(start) && end - snappedStart >= minLen) {
      out.push({
        intervalIndex,
        edge: 'start',
        from: start,
        to: round3(snappedStart),
        deltaSec: round3(snappedStart - start),
      });
      start = round3(snappedStart);
    }

    const snappedEnd = snapToNearest(end, cuts, tolerance);
    if (snappedEnd !== null && round3(snappedEnd) !== round3(end) && snappedEnd - start >= minLen) {
      out.push({
        intervalIndex,
        edge: 'end',
        from: end,
        to: round3(snappedEnd),
        deltaSec: round3(snappedEnd - end),
      });
      end = round3(snappedEnd);
    }
  });
  return out;
}

/** Apply a `snapIntervalsToSceneCuts` result to a copy of the intervals. The
 *  input array is never mutated; unknown indexes are ignored. */
export function applySnapAdjustments<T extends ClipIntervalLike>(
  intervals: T[],
  adjustments: SnapAdjustment[],
): T[] {
  if (!Array.isArray(intervals) || !Array.isArray(adjustments) || adjustments.length === 0) {
    return Array.isArray(intervals) ? [...intervals] : [];
  }
  const next = intervals.map((iv) => ({ ...iv }));
  for (const adj of adjustments) {
    const target = next[adj.intervalIndex];
    if (!target) continue;
    if (adj.edge === 'start') target.startTime = adj.to;
    else target.endTime = adj.to;
  }
  return next;
}

/* ------------------------------------------------------------------ *
 * Orchestrator
 * ------------------------------------------------------------------ */

export interface AssignSignalTransitionsInput {
  /** Raw fetched JSON (or an already-parsed `VideoSignals`, or `null`). */
  signals: unknown;
  clipIntervals: ClipIntervalLike[] | null | undefined;
  /** The project's `content_type` / `content_type_confidence`. When omitted the
   *  ones inside the signals JSON are used. */
  contentType?: string | null;
  contentTypeConfidence?: number | null;
  /** Live crop-keyframe boundaries. Generated keyframe transitions are snapped
   *  onto these, because the editor sanitizes `keyframeTransitions` against the
   *  boundary list — an unsnapped entry would be persisted but never rendered.
   *  Omit to place at raw signal times. */
  keyframeBoundaries?: number[];
  options?: {
    snapToleranceSec?: number;
    marginSec?: number;
    durationSec?: number;
    maxSceneCutTransitions?: number;
    maxEmphasisTransitions?: number;
    minPeakIntensity?: number;
    minPeakSpacingSec?: number;
  };
}

export interface AssignSignalTransitionsResult {
  sceneTransitions: SceneTransition[];
  keyframeTransitions: KeyframeTransition[];
  contentType: ContentType;
  contentTypeConfidence: number | null;
  /** Whether the signals payload was usable at all. */
  hasSignals: boolean;
  stats: {
    sceneCutTransitions: number;
    emphasisTransitions: number;
    sceneCutsAvailable: number;
    peaksAvailable: number;
  };
}

const EMPTY_RESULT = (
  contentType: ContentType,
  contentTypeConfidence: number | null,
  hasSignals: boolean,
): AssignSignalTransitionsResult => ({
  sceneTransitions: [],
  keyframeTransitions: [],
  contentType,
  contentTypeConfidence,
  hasSignals,
  stats: {
    sceneCutTransitions: 0,
    emphasisTransitions: 0,
    sceneCutsAvailable: 0,
    peaksAvailable: 0,
  },
});

/** The single entry point the editor calls. Combines (a) + (b) + (d) into one
 *  replace-everything result, mirroring how `assignRandomTransitions` is
 *  applied. Behavior (c) stays separate (`snapIntervalsToSceneCuts`) because it
 *  edits the timeline rather than decorating it.
 *
 *  Returns an all-empty, `hasSignals: false` result for any unusable input —
 *  never throws, so a null `video_signals_url` project is a pure no-op. */
export function assignSignalTransitions(
  input: AssignSignalTransitionsInput,
): AssignSignalTransitionsResult {
  const opts = input?.options ?? {};
  // Accepts raw fetched JSON or an already-parsed VideoSignals — both go
  // through the same guard.
  const parsed = parseVideoSignals(input?.signals);

  const { contentType, profile } = resolveContentTypeProfile(
    input?.contentType ?? parsed?.content_type ?? null,
    input?.contentTypeConfidence ?? parsed?.content_type_confidence ?? null,
  );
  const confidence = isFiniteNumber(input?.contentTypeConfidence)
    ? input.contentTypeConfidence
    : parsed?.content_type_confidence ?? null;

  if (!parsed) return EMPTY_RESULT(contentType, confidence, false);

  const ivs = validIntervals(input?.clipIntervals);
  if (ivs.length === 0) return EMPTY_RESULT(contentType, confidence, true);

  const snapTargets = Array.isArray(input?.keyframeBoundaries)
    ? input.keyframeBoundaries.filter(isFiniteNumber)
    : undefined;

  const sceneCutKeyframes = assignSceneCutKeyframeTransitions({
    sceneCuts: parsed.scene_cuts,
    clipIntervals: ivs,
    profile,
    snapTargets,
    snapToleranceSec: opts.snapToleranceSec,
    marginSec: opts.marginSec,
    durationSec: opts.durationSec,
    maxTransitions: opts.maxSceneCutTransitions,
  });

  const emphasis = assignEmphasisTransitions({
    peaks: [parsed.peaks.audio, parsed.peaks.motion],
    clipIntervals: ivs,
    profile,
    snapTargets,
    snapToleranceSec: opts.snapToleranceSec,
    marginSec: opts.marginSec,
    durationSec: opts.durationSec,
    maxTransitions: opts.maxEmphasisTransitions,
    minIntensity: opts.minPeakIntensity,
    minSpacingSec: opts.minPeakSpacingSec,
  });

  // Scene-cut placements win over emphasis placements at the same boundary
  // (first wins, matching the sanitizers).
  const mergedKeyframes = sanitizeKeyframeTransitions(
    [...sceneCutKeyframes, ...emphasis.keyframeTransitions],
    [...sceneCutKeyframes, ...emphasis.keyframeTransitions].map((t) => t.atTime),
  );

  return {
    sceneTransitions: emphasis.sceneTransitions,
    keyframeTransitions: mergedKeyframes,
    contentType,
    contentTypeConfidence: confidence,
    hasSignals: true,
    stats: {
      sceneCutTransitions: sceneCutKeyframes.length,
      emphasisTransitions: emphasis.used,
      sceneCutsAvailable: parsed.scene_cuts.length,
      peaksAvailable: parsed.peaks.audio.length + parsed.peaks.motion.length,
    },
  };
}
